// src/lib/sysadminRoles.ts
//
// Canonical list of Auth0 roles that grant full admin privileges within
// THIS application (delldata-reports). Two tiers — both treated equivalently
// inside this codebase:
//
//   - PLATFORM_ADMIN_ROLE ('GeniReports Admin')
//     Grants admin access across EVERY GeniReports application
//     (delldata-reports, future client apps, the planned portal). When you
//     hire a Genovate engineer, give them this one.
//
//   - APP_ADMIN_ROLE ('Delldata Admin')
//     Grants admin access only inside delldata-reports. Use for an
//     embedded admin at a delldata operator — someone who manages the
//     delldata tenant directory but should not see other clients' apps.
//
// Within this codebase the two roles unlock the same things: cross-tenant
// access in canAccessTenant, the System Admin tile, raw-SQL on Custom
// Reports, and any future admin-only feature. The distinction lives at
// the Auth0 role level — assign the role that matches the person's blast
// radius and downstream apps will gate themselves accordingly.
//
// Adding another app-specific admin (e.g. 'BlueSky Admin') in the future
// means appending to ADMIN_ROLES and creating the role in Auth0; no other
// code changes.

export const PLATFORM_ADMIN_ROLE = 'GeniReports Admin';
export const APP_ADMIN_ROLE = 'Delldata Admin';

/**
 * Every Auth0 role that grants full admin within delldata-reports.
 * Order doesn't matter; check is membership not priority.
 */
export const ADMIN_ROLES: readonly string[] = [
  PLATFORM_ADMIN_ROLE,
  APP_ADMIN_ROLE,
];

/**
 * True iff the user's role claim contains any admin-tier role.
 *
 * Tolerant of malformed input: missing/empty arrays yield false rather than
 * throwing, so callers can chain it after a destructure without a guard.
 */
export function hasAdminRole(roles: readonly string[] | undefined | null): boolean {
  if (!roles || roles.length === 0) return false;
  return roles.some((r) => ADMIN_ROLES.includes(r));
}
